/**
 * MealPlanVersionHistory — Phase 2.2B Session 4
 *
 * Backend: GET /api/nutrition/meal-plans/{athlete_id} (returns plan + versions list)
 *           POST /api/nutrition/meal-plans/version/{id}/restore — creates a NEW version
 *           cloning the source's meals/plan_type/dates.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { History, Loader2, RotateCcw, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const PLAN_BADGE = {
  GENERIC: 'bg-zinc-700 text-zinc-200 border-zinc-600',
  REST: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  TRAINING: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',
  MATCH: 'bg-red-500/20 text-red-300 border-red-500/40',
  DOUBLE_SESSION: 'bg-violet-500/20 text-violet-300 border-violet-500/40',
  RTP: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
};

const fmt0 = (v) => v === null || v === undefined ? '—' : Math.round(Number(v));

const MealPlanVersionHistory = ({ athleteId, refreshKey, canRestore = false, onRestored }) => {
  const { api } = useAuth();
  const { language } = useLanguage();
  const [versions, setVersions] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/nutrition/meal-plans/${athleteId}`);
      setVersions(res.data?.versions || []);
      setCurrentId(res.data?.plan?.current_version_id || null);
    } catch {
      setVersions([]); setCurrentId(null);
    } finally {
      setLoading(false);
    }
  }, [api, athleteId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const handleRestore = async (versionId) => {
    if (!window.confirm(language === 'IT' ? 'Ripristinare questa versione come nuova versione attiva?' : 'Restore as a new active version?')) return;
    setRestoring(versionId);
    try {
      await api.post(`/nutrition/meal-plans/version/${versionId}/restore`);
      await load();
      onRestored && onRestored();
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoring(null);
    }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 overflow-hidden" data-testid="mealplan-version-history">
      <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-2">
        <History className="w-4 h-4 text-zinc-400" />
        <h3 className="text-sm font-semibold text-zinc-200">
          {language === 'IT' ? 'Storico versioni piano' : 'Plan version history'}
        </h3>
        <span className="text-xs text-zinc-500 ml-1">({versions.length})</span>
      </div>
      {loading ? (
        <div className="p-8 flex items-center justify-center text-zinc-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> {language === 'IT' ? 'Caricamento…' : 'Loading…'}
        </div>
      ) : versions.length === 0 ? (
        <div className="p-8 text-center text-zinc-500 text-sm" data-testid="mealplan-history-empty">
          {language === 'IT' ? 'Nessuna versione ancora creata.' : 'No versions created yet.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-zinc-950 text-zinc-400 uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left">v</th>
                <th className="px-3 py-2 text-left">{language === 'IT' ? 'Tipo' : 'Type'}</th>
                <th className="px-3 py-2 text-left">{language === 'IT' ? 'Etichetta' : 'Label'}</th>
                <th className="px-3 py-2 text-left">{language === 'IT' ? 'Dal' : 'From'}</th>
                <th className="px-3 py-2 text-right">{language === 'IT' ? 'Calorie' : 'Calories'}</th>
                <th className="px-3 py-2 text-left">{language === 'IT' ? 'Creato' : 'Created'}</th>
                <th className="px-3 py-2 text-center">{language === 'IT' ? 'Stato' : 'Status'}</th>
                {canRestore && <th className="px-3 py-2 text-right" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {versions.map((v) => {
                const isActive = v.id === currentId;
                const created = (v.created_at || '').slice(0, 10);
                return (
                  <tr key={v.id} className={isActive ? 'bg-amber-500/5' : 'hover:bg-zinc-800/40'} data-testid={`version-row-${v.version}`}>
                    <td className="px-3 py-2 text-zinc-100 font-medium">v{v.version}</td>
                    <td className="px-3 py-2">
                      <Badge className={`${PLAN_BADGE[v.plan_type] || ''} text-[10px]`}>{v.plan_type}</Badge>
                    </td>
                    <td className="px-3 py-2 text-zinc-300">{v.label || '—'}</td>
                    <td className="px-3 py-2 text-zinc-200">{v.effective_from}</td>
                    <td className="px-3 py-2 text-right text-zinc-100">{fmt0(v.totals?.calories)} <span className="text-zinc-500">kcal</span></td>
                    <td className="px-3 py-2 text-zinc-500">{created}</td>
                    <td className="px-3 py-2 text-center">
                      {isActive ? (
                        <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40 text-[10px]" data-testid={`version-active-${v.version}`}>
                          <CheckCircle2 className="w-2.5 h-2.5 inline mr-1" />
                          {language === 'IT' ? 'ATTIVA' : 'ACTIVE'}
                        </Badge>
                      ) : v.restored_from_version ? (
                        <span className="text-[10px] text-zinc-500">
                          {language === 'IT' ? 'rip. da v' : 'rest. v'}{v.restored_from_version}
                        </span>
                      ) : (
                        <span className="text-zinc-600 text-[10px]">—</span>
                      )}
                    </td>
                    {canRestore && (
                      <td className="px-3 py-2 text-right">
                        {!isActive && (
                          <Button
                            size="sm" variant="ghost" disabled={restoring === v.id}
                            onClick={() => handleRestore(v.id)}
                            data-testid={`version-restore-${v.version}`}
                            className="text-cyan-400 hover:bg-cyan-500/10 px-2"
                          >
                            {restoring === v.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                            <span className="ml-1 text-[10px]">{language === 'IT' ? 'Ripristina' : 'Restore'}</span>
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default MealPlanVersionHistory;
